import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useVersion } from '../contexts/VersionContext';
import { clarityEvent, gaEvent } from '../analytics';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import useWaitlistSignup from '../hooks/useWaitlistSignup';
import { formatPreOrderDate, getNextPreOrderDate } from '../utils/preOrderDate';
import styles from './Hero.module.css';

const heroImages = [
  '/images/webp/Hero1.webp',
  '/images/webp/Hero2.webp',
  '/images/webp/Hero3.webp',
  '/images/webp/Hero4.webp',
  '/images/webp/Hero5.webp',
];

const heroImages1920 = [
  '/images/webp/Hero1_1920w.webp',
  '/images/webp/Hero2.webp',       // Hero2 is natively 1920×1080
  '/images/webp/Hero3_1920w.webp',
  '/images/webp/Hero4_1920w.webp',
  '/images/webp/Hero5_1920w.webp',
];

const heroImages960 = [
  '/images/webp/Hero1_960w.webp',
  '/images/webp/Hero2_960w.webp',
  '/images/webp/Hero3_960w.webp',
  '/images/webp/Hero4_960w.webp',
  '/images/webp/Hero5_960w.webp',
];

const heroImageAlts = [
  'INCEPTUM: Dawn of Civilizations board game showing Ancient Age settlement with tribesmen engaging in bartering of goods.',
  'INCEPTUM: Dawn of Civilizations board game showing Ancient age wonder, The Temple of Artemis, and a medieval age harbour.',
  'INCEPTUM: Dawn of Civilizations board game showing medieval age feudalistic society with a stone quarry and timer depot.',
  'INCEPTUM: Dawn of Civilizations board game showing thriving medieval cities with the Hagia Sophia and The Taj Mahal',
  'INCEPTUM: Dawn of Civilizations board game showing evolution of multiple civilizations across ancient and medieval ages with the passage of time and many great wonders of the world.',
];

const SHOW_PRICE_PRELAUNCH = false;

export default function Hero({ treatment = null }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const handleWaitlistPopupClose = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    window.history.replaceState(null, '', '/');
  }, []);
  const waitlist = useWaitlistSignup({
    id: 'hero',
    formName: 'hero_waitlist',
    onPopupClose: handleWaitlistPopupClose,
  });
  const [countdownData, setCountdownData] = useState({
    isOpen: false,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const { isImprovedVersion } = useVersion();
  const heroRef = useSectionViewTracking('hero', {
    onView: () => {
      gaEvent('view_promo', {
        page_section: 'hero',
        game_title: 'INCEPTUM: Dawn of Civilizations',
        price: 4399,
        currency: 'INR',
        discount_percent: 35,
      });
      clarityEvent('view_promo');
    },
  });

  const preOrderDate = useMemo(() => getNextPreOrderDate(), []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 10000); // Change image every 10 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const diff = preOrderDate.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdownData({ isOpen: true, days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setCountdownData({ isOpen: false, days, hours, minutes, seconds });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [preOrderDate]);

  const renderWaitlistMessage = (message) => {
    const lines = message.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const shouldShowPrice = SHOW_PRICE_PRELAUNCH || countdownData.isOpen;
  const isCompactTreatment = ['b', 'c', 'd'].includes(treatment);
  const isCompleteFirstScreen = treatment === 'b';
  const isScrollFirstTreatment = ['c', 'd'].includes(treatment);
  const showInlineWaitlist = !treatment || treatment === 'a' || treatment === 'b';
  return (
    <section
      ref={heroRef}
      className={`${styles.hero} ${isImprovedVersion ? styles.heroImproved : ''} ${isCompactTreatment ? styles.heroCompact : ''} ${isScrollFirstTreatment ? styles.heroScrollFirst : ''}`}
      id="home"
    >
      <div className={styles.heroImage}>
        {heroImages.map((image, index) => (
          <img
            key={image}
            src={heroImages960[index]}
            srcSet={`${heroImages960[index]} 960w, ${heroImages1920[index]} 1920w, ${image} 3840w`}
            sizes="100vw"
            alt={heroImageAlts[index]} /* COPY_010–COPY_016 */
            width={1920}
            height={1080}
            fetchPriority={index === 0 ? 'high' : undefined}
            loading={index === 0 ? undefined : 'lazy'}
            className={`${styles.backgroundImage} ${
              index === currentImageIndex ? styles.active : ''
            }`}
          />
        ))}
        <div className={styles.overlay}></div>
        
        <div className={styles.heroContent}>
          <div className={styles.boxImage}>
            <img
              src="/images/webp/BoxFront_1045x820.webp"
              srcSet="/images/webp/BoxFront_520w.webp 520w, /images/webp/BoxFront_1045x820.webp 1045w"
              sizes="(max-width: 768px) 50vw, 45vw"
              alt="INCEPTUM: Dawn of Civilizations - board game box"
              width={1045}
              height={820}
              fetchPriority="high"
              className={styles.productBox}
              draggable={false}
            />
            {/* TEMP: hide box tagline below game box - restore when needed
            <p className={styles.boxTagline}>
              Designed for players who enjoy games that linger long after the final turn.
            </p>
            */}
          </div>
          
          <div className={styles.productDetails}>
            <div className={styles.heroHeading}>
              <h1 className={styles.heroTitle}>INCEPTUM<span className={styles.heroSubtitle}>Dawn of Civilizations</span></h1>
            </div>
            {isCompactTreatment ? (
              <p className={styles.description}>
                <strong>A medium-heavy, engine-building strategy board game where asymmetry, timing, and long-term planning decide everything.</strong>
              </p>
            ) : (
              <p className={styles.description}>
                <strong>A medium-heavy, engine-building strategy board game where asymmetry, timing, and long-term planning decide everything.</strong><br/><br/>
                No combat. Irreversible choices. What you risk, and what you let go of, defines your civilization.
              </p>
            )}

            {isCompleteFirstScreen && (
              <div className={styles.heroFacts} aria-label="Game details">
                <span><strong>2–4</strong> Players</span>
                <span><strong>60–120</strong> Minutes</span>
                <span><strong>12+</strong> Ages</span>
              </div>
            )}
            
            <div className={styles.preOrderPanel}>
              {shouldShowPrice && (
                <div className={styles.priceSection}>
                  <div className={styles.priceContainer}>
                    <span className={`${styles.oldPrice} ${isImprovedVersion ? styles.oldPriceImproved : ''}`}>₹6799</span>
                    <span className={`${styles.newPrice} ${isImprovedVersion ? styles.newPriceImproved : ''}`}>₹4399</span>
                    {/* <span className={`${styles.discountBadge} ${isImprovedVersion ? styles.discountBadgeImproved : ''}`}>35% OFF</span> */}
                  </div>
                  {/* <p className={styles.priceNote}><strong>(First print run discount - Future editions may be priced higher!)</strong></p> */}
                </div>
              )}

              <div className={styles.preOrderInfo}>
                <p className={styles.preOrderDate}>
                  Pre-orders open in {formatPreOrderDate(preOrderDate)}
                </p>

                {showInlineWaitlist && (
                  <form className={styles.waitlistForm} onSubmit={waitlist.handleSubmit}>
                    <input
                      id="hero-waitlist-email"
                      type="email"
                      value={waitlist.email}
                      onFocus={waitlist.handleEmailFocus}
                      onChange={waitlist.handleEmailChange}
                      placeholder="Enter your email"
                      className={styles.waitlistInput}
                      required
                      aria-label="Email for waitlist"
                    />
                    <button
                      type="submit"
                      className={styles.waitlistButton}
                      disabled={waitlist.status === 'submitting' || !waitlist.recaptchaEnabled}
                    >
                      Join the waitlist!
                    </button>
                  </form>
                )}
                {showInlineWaitlist && waitlist.showNewsletterOptIn && (
                  <label className={styles.newsletterOptIn} htmlFor="hero-waitlist-newsletter">
                    <input
                      id="hero-waitlist-newsletter"
                      type="checkbox"
                      checked={waitlist.newsletterOptIn}
                      onChange={(event) => waitlist.setNewsletterOptIn(event.target.checked)}
                      className={styles.newsletterOptInBox}
                    />
                    <span>Subscribe to our newsletter</span>
                  </label>
                )}
                {showInlineWaitlist && (
                  <div className={styles.waitlistMeta}>
                    {waitlist.recaptchaEnabled && waitlist.showRecaptcha ? (
                    <div className={styles.waitlistRecaptcha}>
                      <ReCAPTCHA
                        ref={waitlist.recaptchaRef}
                        sitekey={waitlist.recaptchaSiteKey}
                        onChange={waitlist.handleRecaptchaChange}
                        onExpired={waitlist.handleRecaptchaExpired}
                        onErrored={waitlist.handleRecaptchaErrored}
                        theme="dark"
                      />
                    </div>
                    ) : !waitlist.recaptchaEnabled ? (
                      <p className={styles.waitlistStatusError}>reCAPTCHA is unavailable right now.</p>
                    ) : null}

                    {waitlist.recaptchaError && (
                      <p className={styles.waitlistStatusError}>{waitlist.recaptchaError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.actions}>
              {/* <button
                className={`${styles.buyButton} ${isImprovedVersion ? styles.buyButtonImproved : ''}`}
                onClick={handleBuyNowClick}
              >
                Pre Order Now
              </button> */}
              {/* <p className={styles.priceNote}>
                <strong>(First print run discount - Future editions may be priced higher!)</strong>
              </p> */}
            </div>


          </div>
        </div>
      </div>

      {waitlist.popup.isOpen && (
        <div className={styles.waitlistPopupOverlay} role="dialog" aria-modal="true" aria-live="polite">
          <div className={styles.waitlistPopupCard}>
            {waitlist.popup.phase === 'loading' ? (
              <>
                <div className={styles.waitlistPopupSpinner} />
                <h3 className={styles.waitlistPopupTitle}>Please wait</h3>
                <p className={styles.waitlistPopupMessage}>{waitlist.popup.message}</p>
              </>
            ) : (
              <>
                {waitlist.popup.phase === 'success' && (
                  <div className={styles.waitlistPopupSuccessIcon}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
                <h3 className={styles.waitlistPopupTitle}>
                  {waitlist.popup.phase === 'success' ? "You\u2019re on the list" : 'Something went wrong'}
                </h3>
                <p className={styles.waitlistPopupMessage}>
                  {renderWaitlistMessage(waitlist.popup.message)}
                </p>
                <button
                  ref={waitlist.popupButtonRef}
                  type="button"
                  className={styles.waitlistPopupButton}
                  onClick={waitlist.closePopup}
                >
                  Got it
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
